import { useId } from 'react';
import { BRAND_FIELD_DEFINITIONS, BRAND_LIMITS, type EditPageListKey } from '@mg/shared';
import { Card, CardBody, CardHeader } from '../../../components/ui/Card';
import { SECTION_COPY, TAG_PLACEHOLDERS } from '../../brand/brandCopy';
import { BRAND_SECTION_IDS } from '../../brand/editPages';
import { BrandFieldShell } from '../../brand/fields/BrandFieldShell';
import { TagListInput, type TagTone } from '../../brand/fields/TagListInput';
import { brandFieldDescribedBy } from '../../brand/fields/fieldParts';
import { normalizeHashtag } from '../../brand/tagRules';
import { EditSection } from '../EditSection';
import type { BrandInformationFormState } from './useBrandInformationForm';

type VocabularyKey = Exclude<EditPageListKey, 'writingGuidelines'>;

/** Keywords, Social hashtags and Catchphrases are teal; Always say green with a check; Never say red with a slash (spec:263). */
const TONE: Record<VocabularyKey, TagTone> = {
  keywords: 'teal',
  alwaysSay: 'always',
  neverSay: 'never',
  socialHashtags: 'teal',
  catchphrases: 'teal',
};

/** One Vocabulary list: its label and source tags, its hint, then the tags with the "Add…" box. */
function VocabularyList({
  fieldKey,
  form,
  canEdit,
}: {
  fieldKey: VocabularyKey;
  form: BrandInformationFormState;
  canEdit: boolean;
}) {
  const id = useId();
  const def = BRAND_FIELD_DEFINITIONS[fieldKey];
  const { sources, aiSuggested } = form.listProvenance(fieldKey);
  return (
    // Read-only, there is no "Add…" box for the label to name: the list carries the name itself.
    <BrandFieldShell
      id={id}
      group={!canEdit}
      label={def.label}
      hint={def.hint}
      sources={sources}
      aiSuggested={aiSuggested}
      readOnly={!canEdit}
    >
      <TagListInput
        id={id}
        label={def.label}
        addPlaceholder={TAG_PLACEHOLDERS[fieldKey]}
        items={form.lists[fieldKey]}
        onChange={(next) => form.setList(fieldKey, next)}
        tone={TONE[fieldKey]}
        // Today's hashtag rule: one leading "#" (as BrandAssetsEditor has it).
        normalize={fieldKey === 'socialHashtags' ? normalizeHashtag : undefined}
        maxItems={BRAND_LIMITS.tagsPerList}
        maxLength={BRAND_LIMITS.tag}
        readOnly={!canEdit}
        disabled={form.saving}
        describedBy={brandFieldDescribedBy(id, { hint: def.hint, sources, aiSuggested })}
      />
    </BrandFieldShell>
  );
}

/**
 * Vocabulary (spec:186, :259-263), editable on the Edit page for admins (spec:176, C41): Keywords,
 * then Always say and Never say side by side, then Social hashtags, then Catchphrases and slogans.
 * Adding or removing a tag is a change; typing in an "Add…" box is not (spec:197). Stored items that
 * break today's rules (hashtags and phrases saved on the old page, E4) are shown as they are and can
 * be removed. Read-only for a member.
 */
export function VocabularyCard({ form, canEdit }: { form: BrandInformationFormState; canEdit: boolean }) {
  const copy = SECTION_COPY.vocabulary;
  return (
    <EditSection page="brand" id={BRAND_SECTION_IDS.vocabulary}>
      <Card>
        <CardHeader title={copy.title} description={copy.description} />
        <CardBody className="space-y-6">
          <VocabularyList fieldKey="keywords" form={form} canEdit={canEdit} />
          <div className="grid gap-6 md:grid-cols-2">
            <VocabularyList fieldKey="alwaysSay" form={form} canEdit={canEdit} />
            <VocabularyList fieldKey="neverSay" form={form} canEdit={canEdit} />
          </div>
          <VocabularyList fieldKey="socialHashtags" form={form} canEdit={canEdit} />
          <VocabularyList fieldKey="catchphrases" form={form} canEdit={canEdit} />
        </CardBody>
      </Card>
    </EditSection>
  );
}
